/**
 * Paddle client integration — one module for the whole Paddle surface.
 *
 * Paddle must init once and be shared across many payment callers, so the SDK
 * instance is a module-level singleton (init deduped via a shared promise). The
 * usePaddle() hook exposes it to React with an `isReady` flag plus the checkout
 * actions; pollBalanceAfterPaddleCharge is a plain function (no instance needed).
 */

import { useCallback, useEffect, useState } from "react";
import {
  initializePaddle as sdkInitializePaddle,
  CheckoutEventNames,
  type Paddle,
  type Environments,
  type PaddleEventData,
  type CheckoutOpenOptions,
} from "@paddle/paddle-js";
import { APP_ENV, PADDLE_TOKEN } from "@/config/constants";
import { apiClient } from "@/lib/api/client";
import { queryClient } from "@/providers/query-provider";
import { paymentKeys } from "@/queries/keys";
import { PaymentSourceType } from "@/features/subscription/types";
import { refreshPaymentSources } from "@/features/subscription/payment-queries";

// Angular parity (select-source-card.component.ts): listSources(paddle) returns []
// until the subscription.activated webhook persists the method server-side, which
// lands ~1s after checkout.completed. Poll every 1s, up to 5 tries (~5s window),
// stopping the instant the Paddle list is non-empty.
const PADDLE_SOURCES_POLL_MS = 1000;
const PADDLE_SOURCES_POLL_MAX = 5;

function reconcilePaddleSources(): void {
  let attempts = 0;
  const tick = async (): Promise<void> => {
    attempts += 1;
    await refreshPaymentSources();
    const paddle = queryClient.getQueryData<unknown[]>(
      paymentKeys.sources(PaymentSourceType.Paddle),
    );
    const hasPaddle = Array.isArray(paddle) && paddle.length > 0;
    if (hasPaddle || attempts >= PADDLE_SOURCES_POLL_MAX) return;
    window.setTimeout(() => void tick(), PADDLE_SOURCES_POLL_MS);
  };
  void tick();
}

/**
 * Use the app's canonical environment (NEXT_PUBLIC_APP_ENV), matching every other
 * SDK (Intercom, analytics — see constants.ts). NOT import.meta.env.PROD, which is
 * true for any production BUILD (e.g. a staging/preview build) and would pair the
 * wrong Paddle environment with the token.
 */
const PADDLE_ENVIRONMENT: Environments =
  APP_ENV === "production" ? "production" : "sandbox";

const CHECKOUT_SETTINGS = {
  displayMode: "overlay" as const,
  frameTarget: "parent" as const,
  allowedPaymentMethods: ["paypal" as const],
  theme: "light" as const,
  locale: "en",
  showAddDiscounts: false,
  showAddTaxId: false,
};

// Module-level singleton: one Paddle instance per app, shared by every usePaddle().
let paddleInstance: Paddle | null = null;
let initPromise: Promise<Paddle | null> | null = null;
// Paddle's eventCallback is global to the instance; route each open checkout's
// events to whichever waiter opened it via this single active-handler ref.
let activeCheckoutHandler: ((event: PaddleEventData) => void) | null = null;
// Only the add-source (Connect PayPal) flow needs the sources list to poll on
// completion — a subscription purchase or balance charge does not. Set per-open.
let pollSourcesOnComplete = false;

/** Idempotent init — concurrent callers share one in-flight promise. */
function ensurePaddle(): Promise<Paddle | null> {
  if (paddleInstance) return Promise.resolve(paddleInstance);
  if (initPromise) return initPromise;

  initPromise = sdkInitializePaddle({
    environment: PADDLE_ENVIRONMENT,
    token: PADDLE_TOKEN,
    checkout: { settings: CHECKOUT_SETTINGS },
    eventCallback: (event) => {
      if (event.name === CheckoutEventNames.CHECKOUT_COMPLETED) {
        paddleInstance?.Checkout.close();
        // Only the add-source flow polls. Lives here, not in a component, so it
        // survives select-source closing the moment the overlay finishes.
        if (pollSourcesOnComplete) {
          pollSourcesOnComplete = false;
          reconcilePaddleSources();
        }
      }
      activeCheckoutHandler?.(event);
      // Backward-compat bus — add-source.tsx listens to "paddle:event".
      window.dispatchEvent(new CustomEvent("paddle:event", { detail: event }));
    },
  })
    .then((instance) => {
      paddleInstance = instance ?? null;
      return paddleInstance;
    })
    .catch((err) => {
      initPromise = null; // allow a later retry
      throw err;
    });

  return initPromise;
}

export type PaddleCheckoutStatus =
  | "completed"
  | "closed"
  | "failed"
  | "timeout";

/** Thrown by openCheckoutAndWait on any non-completed terminal outcome. */
export class PaddleCheckoutError extends Error {
  constructor(
    public readonly status: Exclude<PaddleCheckoutStatus, "completed">,
    message: string,
  ) {
    super(message);
    this.name = "PaddleCheckoutError";
  }
}

export interface PaddleCheckoutResult {
  status: "completed";
  transactionId: string;
}

interface PaddleCustomerData {
  email: string;
  name: string;
  address: {
    firstLine: string;
    city: string;
    region: string;
    postalCode: string;
    countryCode: string;
  };
}

function openOverlay(
  transactionId: string,
  customerData?: PaddleCustomerData,
): void {
  if (!paddleInstance) throw new Error("Paddle is not ready");
  const params: CheckoutOpenOptions = {
    transactionId,
    settings: { allowLogout: false, allowedPaymentMethods: ["paypal"] },
  };
  if (customerData) params.customer = customerData;
  paddleInstance.Checkout.open(params);
}

/**
 * React hook over the shared Paddle instance.
 *
 * - initializes Paddle on first mount (deduped across consumers)
 * - `isReady` flips true once the instance is available
 * - `openCheckout` opens the PayPal overlay (fire-and-forget)
 * - `openCheckoutAndWait` resolves on checkout.completed / rejects on
 *   closed|failed|error|timeout. Rejection messages are "checkout_closed" |
 *   "checkout_error" | "checkout_timeout" so callers matching those strings keep working.
 *   checkout.payment.failed is non-terminal (overlay stays open) → onPaymentFailed.
 */
export function usePaddle() {
  const [isReady, setIsReady] = useState<boolean>(() => !!paddleInstance);

  useEffect(() => {
    // Already initialized — the useState initializer already reported ready.
    if (paddleInstance) return;
    let cancelled = false;
    ensurePaddle()
      .then((instance) => {
        if (!cancelled && instance) setIsReady(true);
      })
      .catch(() => {
        // Non-web / SDK load failure — isReady stays false; callers gate on it.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const openCheckout = useCallback(
    (
      transactionId: string,
      customerData?: PaddleCustomerData,
      opts?: { refreshSourcesOnComplete?: boolean },
    ) => {
      // Add-source (Connect PayPal) sets this so the module callback polls the
      // sources list once the overlay completes; other checkouts leave it off.
      pollSourcesOnComplete = opts?.refreshSourcesOnComplete ?? false;
      if (paddleInstance) {
        openOverlay(transactionId, customerData);
        return;
      }
      // Not ready yet (e.g. a deep-link opening checkout on mount) — init, then open.
      ensurePaddle()
        .then((instance) => {
          if (instance) openOverlay(transactionId, customerData);
        })
        .catch(() => {
          // init failed — nothing to open
        });
    },
    [],
  );

  const openCheckoutAndWait = useCallback(
    (
      transactionId: string,
      opts?: {
        timeoutMs?: number;
        onPaymentFailed?: (event: PaddleEventData) => void;
      },
    ): Promise<PaddleCheckoutResult> => {
      const timeoutMs = opts?.timeoutMs ?? 5 * 60_000;
      // Subscription purchases / charges never poll the sources list.
      pollSourcesOnComplete = false;
      return new Promise<PaddleCheckoutResult>((resolve, reject) => {
        let settled = false;
        let timer: ReturnType<typeof setTimeout>;
        const settle = (fn: () => void) => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          activeCheckoutHandler = null;
          fn();
        };

        activeCheckoutHandler = (event: PaddleEventData) => {
          switch (event.name) {
            case CheckoutEventNames.CHECKOUT_COMPLETED: {
              const completedTxn =
                (event.data as { transaction_id?: string } | undefined)
                  ?.transaction_id ?? transactionId;
              settle(() =>
                resolve({ status: "completed", transactionId: completedTxn }),
              );
              break;
            }
            case CheckoutEventNames.CHECKOUT_CLOSED:
              // Message "checkout_closed" — callers match on it to detect
              // user-cancellation and suppress the failure toast.
              settle(() =>
                reject(new PaddleCheckoutError("closed", "checkout_closed")),
              );
              break;
            case CheckoutEventNames.CHECKOUT_FAILED:
            case CheckoutEventNames.CHECKOUT_ERROR:
              settle(() =>
                reject(new PaddleCheckoutError("failed", "checkout_error")),
              );
              break;
            case CheckoutEventNames.CHECKOUT_PAYMENT_FAILED:
            case CheckoutEventNames.CHECKOUT_PAYMENT_ERROR:
              // Non-terminal: the user can retry inside the overlay. Surface, keep waiting.
              opts?.onPaymentFailed?.(event);
              break;
            default:
              break;
          }
        };

        timer = setTimeout(
          () =>
            settle(() =>
              reject(new PaddleCheckoutError("timeout", "checkout_timeout")),
            ),
          timeoutMs,
        );

        // Init if needed, then open — handler + timeout are already armed above.
        ensurePaddle()
          .then((instance) => {
            if (!instance) {
              settle(() => reject(new Error("Paddle is not ready")));
              return;
            }
            try {
              openOverlay(transactionId);
            } catch (err) {
              settle(() => reject(err));
            }
          })
          .catch((err) => settle(() => reject(err)));
      });
    },
    [],
  );

  return { isReady, openCheckout, openCheckoutAndWait };
}

/**
 * Polls the balance endpoint after a Paddle PayPal charge until either:
 *   (a) the balance reflects the charge (balance >= expectedMinBalance), or
 *   (b) maxAttempts is reached.
 * Returns true on reconciled, false on timeout.
 *
 * Webhook delivery from Paddle to the Speak server can take several seconds — this
 * bridges that lag so the upload flow can proceed rather than blocking on a stale
 * balance read. Does not touch the Paddle instance, so it stays a plain function.
 */
export async function pollBalanceAfterPaddleCharge(params: {
  expectedMinBalance: number;
  maxAttempts?: number;
  intervalMs?: number;
}): Promise<boolean> {
  const { expectedMinBalance, maxAttempts = 8, intervalMs = 1500 } = params;
  for (let i = 0; i < maxAttempts; i++) {
    try {
      const res = await apiClient.get<{ balance: number }>("/payment/balance");
      if (res.success && res.data && res.data.balance >= expectedMinBalance)
        return true;
    } catch {
      // Treat network error as not-yet-reconciled; keep polling.
    }
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }
  return false;
}
